import { Injectable, inject, signal } from "@angular/core";
import { OfflineStorageService } from "../../offline/offline-storage.service";

/**
 * TenantCapabilitiesService — Exposes reactive signals for tenant feature availability.
 *
 * Reads serviceMode and enabledModules from cached vertical config to determine
 * which features should be visible in the UI. This prevents showing options that
 * will be rejected by the backend (e.g., settlement, inventory, GPS tracking).
 *
 * Must call `load()` after initial download completes or on app init.
 */
@Injectable({ providedIn: "root" })
export class TenantCapabilitiesService {
  private readonly storage = inject(OfflineStorageService);

  /** Whether the settlement module is available (Business plan) */
  readonly settlementAvailable = signal(false);

  /** Whether the inventory module is available (Business + Trial plans) */
  readonly inventoryAvailable = signal(false);

  /** Whether GPS tracking is available (gps_tracking module — Business plan only) */
  readonly gpsTrackingAvailable = signal(false);

  /**
   * Whether the inspection points module is available (inspection_points add-on).
   * inspection_points NO mapea a un PlanModule: su gate es por add-on. El
   * initial-download refleja el add-on activo en el CSV de enabledModules local,
   * así que aquí se lee igual que los demás módulos.
   */
  readonly inspectionPointsAvailable = signal(false);

  /**
   * Whether the home care vertical is available (home_care_suite add-on).
   * Igual que inspection_points: es add-on sin PlanModule, el initial-download
   * lo refleja en el CSV local de enabledModules.
   */
  readonly homeCareAvailable = signal(false);

  /** Tenant's service mode: 'field' | 'in_house' | null (unknown/not loaded) */
  readonly serviceMode = signal<string | null>(null);

  /** Tenant's vertical identifier (e.g., "hvac", "fumigation") */
  readonly vertical = signal<string | null>(null);

  /**
   * Whether technicians can self-create work orders from the app
   * (verticalConfig.technicianSelfCreateOrders). Default false (fail-closed).
   * @see spec technician-self-created-work-order
   */
  readonly technicianSelfCreateOrders = signal(false);

  /**
   * Load capabilities from cached tenant config.
   * Call this after initial download or on view entry.
   */
  async load(): Promise<void> {
    const config = await this.storage.getVerticalConfig();
    if (!config) return;

    const mode = config.serviceMode ?? null;
    this.serviceMode.set(mode);
    this.vertical.set(config.vertical ?? null);

    const modules = config.enabledModules?.split(",") ?? [];
    this.settlementAvailable.set(modules.includes("settlement"));
    this.inventoryAvailable.set(modules.includes("inventory_basic"));
    this.gpsTrackingAvailable.set(modules.includes("gps_tracking"));
    this.inspectionPointsAvailable.set(modules.includes("inspection_points"));
    this.homeCareAvailable.set(modules.includes("home_care_suite"));
    // SQLite guarda el flag como 0/1; cualquier valor truthy = habilitado.
    this.technicianSelfCreateOrders.set(!!config.technicianSelfCreateOrders);
  }
}
